import { Graphics } from "pixi.js";

import { Colors, Spacing } from "../../config/DesignConstants";
import { LayerName } from "../../config/layers";
import BaseEntity from "../../core/entity/BaseEntity";
import { V, V2d } from "../../core/Vector";
import { TextAnchor, VectorText, VerticalAnchor } from "../text/VectorText";

export interface UpgradeCardOptions {
  id: string;
  name: string;
  description: string;
  rarity: "common" | "rare" | "epic";
  position?: V2d;
  onClick?: (id: string) => void;
  layer?: LayerName;
}

export class UpgradeCard extends BaseEntity {
  pausable = false; // Cards should work even when game is paused

  private background: Graphics;
  private border: Graphics;
  private titleText: VectorText;
  private descriptionTexts: VectorText[] = [];
  private rarityText: VectorText;

  private onClickCallback?: (id: string) => void;
  private isHovered: boolean = false;
  private isPressed: boolean = false;

  private cardWidth = 200;
  private cardHeight = 250;
  private padding = Spacing.MD;

  private bounds: { x: number; y: number; width: number; height: number } = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  };

  constructor(private options: UpgradeCardOptions) {
    super();

    this.onClickCallback = options.onClick;

    // Create background graphics
    this.background = new Graphics();
    if (options.layer) {
      (this.background as any).layerName = options.layer;
    }

    // Create border graphics (separate for glow effect)
    this.border = new Graphics();
    if (options.layer) {
      (this.border as any).layerName = options.layer;
    }

    // Create title text
    this.titleText = new VectorText({
      text: options.name.toUpperCase(),
      scale: 2.4,
      strokeWidth: 2,
      color: this.getRarityColor(),
      anchor: TextAnchor.CENTER,
      verticalAnchor: VerticalAnchor.TOP,
      layer: options.layer,
    });

    // Create description text (may need to wrap)
    const descriptionLines = this.wrapText(options.description, 15);
    for (const line of descriptionLines) {
      const text = new VectorText({
        text: line,
        scale: 2.2,
        strokeWidth: 3,
        color: Colors.CYAN,
        anchor: TextAnchor.CENTER,
        verticalAnchor: VerticalAnchor.TOP,
        layer: options.layer,
      });
      this.descriptionTexts.push(text);
    }

    // Create rarity text
    this.rarityText = new VectorText({
      text: options.rarity.toUpperCase(),
      scale: 1.5,
      strokeWidth: 2,
      color: this.getRarityColor(),
      anchor: TextAnchor.CENTER,
      verticalAnchor: VerticalAnchor.BOTTOM,
      layer: options.layer,
    });

    // Position if provided
    if (options.position) {
      this.updatePosition(options.position);
    }
  }

  private getRarityColor(): number {
    switch (this.options.rarity) {
      case "common":
        return Colors.GREEN;
      case "rare":
        return Colors.PURPLE;
      case "epic":
        return Colors.ORANGE;
      default:
        return Colors.WHITE;
    }
  }

  private wrapText(text: string, maxCharsPerLine: number): string[] {
    const words = text.split(" ");
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
      if (
        (currentLine + " " + word).trim().length <= maxCharsPerLine ||
        currentLine === ""
      ) {
        currentLine = currentLine ? currentLine + " " + word : word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  }

  private updatePosition(position: V2d): void {
    this.background.position.set(position.x, position.y);
    this.border.position.set(position.x, position.y);

    // Update title position
    this.titleText.position = position.add(
      V(0, -this.cardHeight / 2 + this.padding),
    );

    // Update description positions
    const descriptionStartY = -this.cardHeight / 2 + 70;
    this.descriptionTexts.forEach((text, index) => {
      text.position = position.add(V(0, descriptionStartY + index * 32));
    });

    // Update rarity position
    this.rarityText.position = position.add(
      V(0, this.cardHeight / 2 - this.padding),
    );

    this.updateBounds();
  }

  private updateBounds(): void {
    if (this.sprite) {
      const position = this.getPosition();
      this.bounds = {
        x: position.x - this.cardWidth / 2,
        y: position.y - this.cardHeight / 2,
        width: this.cardWidth,
        height: this.cardHeight,
      };
    }
  }

  private isPointInside(x: number, y: number): boolean {
    return (
      x >= this.bounds.x &&
      x <= this.bounds.x + this.bounds.width &&
      y >= this.bounds.y &&
      y <= this.bounds.y + this.bounds.height
    );
  }

  private redrawCard(): void {
    const rarityColor = this.getRarityColor();

    // Clear graphics
    this.background.clear();
    this.border.clear();

    // Draw background
    this.background.rect(
      -this.cardWidth / 2,
      -this.cardHeight / 2,
      this.cardWidth,
      this.cardHeight,
    );
    this.background.fill({
      color: Colors.BLACK,
      alpha: 0.8,
    });

    // Draw border
    const borderWidth = this.isPressed ? 3 : this.isHovered ? 4 : 2;
    this.border.rect(
      -this.cardWidth / 2,
      -this.cardHeight / 2,
      this.cardWidth,
      this.cardHeight,
    );
    this.border.stroke({
      color: rarityColor,
      width: borderWidth,
    });

    // Add glow effect when hovered
    if (this.isHovered && !this.isPressed) {
      this.border.rect(
        -this.cardWidth / 2,
        -this.cardHeight / 2,
        this.cardWidth,
        this.cardHeight,
      );
      this.border.stroke({
        color: rarityColor,
        width: 8,
        alpha: 0.3,
      });
    }

    // Update text thickness on hover
    const textStrokeWidth = this.isHovered ? 4 : 3;
    this.titleText.strokeWidth = textStrokeWidth;
    for (const text of this.descriptionTexts) {
      text.strokeWidth = this.isHovered ? 4 : 3;
    }

    this.rarityText.strokeWidth = textStrokeWidth;
  }

  onAdd(): void {
    this.sprite = this.background;
    this.sprites = [this.background, this.border];

    // Add all text elements
    this.addChildren(this.titleText, this.rarityText, ...this.descriptionTexts);

    this.redrawCard();
  }

  onRender(): void {
    // Check hover state
    if (this.game) {
      const mousePos = this.game.io.mousePosition;
      const wasHovered = this.isHovered;
      this.isHovered = this.isPointInside(mousePos.x, mousePos.y);

      if (wasHovered !== this.isHovered) {
        this.redrawCard();
      }
    }
  }

  onMouseDown(): void {
    if (this.game) {
      const mousePos = this.game.io.mousePosition;
      if (this.isPointInside(mousePos.x, mousePos.y)) {
        this.isPressed = true;
        this.redrawCard();
      }
    }
  }

  onMouseUp(): void {
    if (this.isPressed) {
      this.isPressed = false;
      this.redrawCard();
    }
  }

  onClick(): void {
    if (this.game) {
      const mousePos = this.game.io.mousePosition;
      if (this.isPointInside(mousePos.x, mousePos.y)) {
        this.onClickCallback?.(this.options.id);
      }
    }
  }

  setPosition(position: V2d): void {
    this.updatePosition(position);
  }
}
